import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "aero:bg-popover aero:text-popover-foreground aero:flex aero:h-full aero:w-full aero:flex-col aero:overflow-hidden aero:!rounded-md",
        className
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="aero:sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent className="aero:overflow-hidden aero:p-0">
        <Command className="aero:[&_[cmdk-group-heading]]:text-muted-foreground aero:**:data-[slot=command-input-wrapper]:h-12 aero:[&_[cmdk-group-heading]]:px-2 aero:[&_[cmdk-group-heading]]:font-medium aero:[&_[cmdk-group]]:px-2 aero:[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 aero:[&_[cmdk-input-wrapper]_svg]:h-5 aero:[&_[cmdk-input-wrapper]_svg]:w-5 aero:[&_[cmdk-input]]:h-12 aero:[&_[cmdk-item]]:px-2 aero:[&_[cmdk-item]]:py-3 aero:[&_[cmdk-item]_svg]:h-5 aero:[&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="aero:flex aero:h-9 aero:items-center aero:gap-2 aero:border-b aero:px-3"
    >
      <SearchIcon className="aero:size-4 aero:shrink-0 aero:opacity-50" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          "aero:placeholder:text-muted-foreground aero:flex aero:h-10 aero:w-full aero:!rounded-md aero:bg-transparent aero:py-3 aero:text-sm aero:outline-hidden aero:disabled:cursor-not-allowed aero:disabled:opacity-50",
          className
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "aero:max-h-[300px] aero:scroll-py-1 aero:overflow-x-hidden aero:overflow-y-auto",
        className
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="aero:py-6 aero:text-center aero:text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "aero:text-foreground aero:[&_[cmdk-group-heading]]:text-muted-foreground aero:overflow-hidden aero:p-1 aero:[&_[cmdk-group-heading]]:px-2 aero:[&_[cmdk-group-heading]]:py-1.5 aero:[&_[cmdk-group-heading]]:text-xs aero:[&_[cmdk-group-heading]]:font-medium",
        className
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("aero:bg-border aero:-mx-1 aero:h-px", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "aero:data-[selected=true]:bg-neutral-100 aero:data-[selected=true]:text-foreground aero:[&_svg:not([class*='text-'])]:text-muted-foreground aero:relative aero:flex aero:cursor-default aero:items-center aero:gap-2 aero:!rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-hidden aero:select-none aero:data-[disabled=true]:pointer-events-none aero:data-[disabled=true]:opacity-50 aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "aero:text-muted-foreground aero:ml-auto aero:text-xs aero:tracking-widest",
        className
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
